import styled from '@emotion/native';
import {
  allowedExtensionTypeMap,
  type ContentType,
} from '@quromedical/fhir-common';
import { Col, Row } from 'components/base';
import { Button, Icon, Spacer, Text } from 'design-system';
import { BASE_SIZE, MARGIN, useTheme } from 'design-system/theme';
import { useAudioPlayer } from 'expo-audio';
import { useVideoPlayer, VideoView } from 'expo-video';
import { handleDownload } from 'helpers/download';
import { useIsSmallerThan } from 'hooks/useResponsive';
import type React from 'react';
import { useState } from 'react';
import { Image, Pressable, useWindowDimensions, View } from 'react-native';
import {
  Bubble,
  type BubbleProps,
  type IMessage,
  MessageImage,
  type RenderMessageAudioProps,
  type RenderMessageImageProps,
  type RenderMessageTextProps,
  type RenderMessageVideoProps,
  Time,
  type TimeProps,
} from 'react-native-gifted-chat';
import { strings } from 'strings';

export interface IMessageProps extends IMessage {
  contact?: string;
  documentFile?: string;
  contentType?: ContentType;
}

const ChatEmpty = styled.View(() => ({
  transform: [{ scaleY: -1 }],
  alignItems: 'center',
  margin: 'auto',
}));

export const ChatEmptyMessage: React.FC = () => {
  return (
    <ChatEmpty>
      <Text>No messages available</Text>
      <Text>Start a conversation by sending a template.</Text>
    </ChatEmpty>
  );
};

export const ChatBubble: React.FC<BubbleProps<IMessage>> = (props) => {
  const theme = useTheme();
  const { width, height } = useWindowDimensions();

  const isSmall = useIsSmallerThan('md');
  const initialWidth = isSmall ? width / 2 : width / 2.4;
  const initialHeight = isSmall ? height / 2 : height / 2.5;

  const wrapperStyle = {
    left: {
      // backgroundColor: '#F7F6FB',
      maxWidth: initialWidth,
      marginBottom: MARGIN['3xs'],
      backgroundColor: theme.color['message-background-right'],
    },
    right: {
      // backgroundColor: '#D9FDD2',
      maxWidth: initialWidth,
      marginBottom: MARGIN['3xs'],
      backgroundColor: theme.color['message-background-left'],
    },
  };

  const DownloadWrapper = styled(Row)({
    position: 'absolute',
    right: 0,
    zIndex: 1,
    margin: 10,
  });
  const renderTime = (props: TimeProps<IMessage>) => (
    <Time
      {...props}
      timeTextStyle={{
        left: {
          color: theme.color['message-pending'],
        },
        right: {
          color: theme.color['message-pending'],
        },
      }}
    />
  );

  const Chat: React.FC<Partial<IMessage>> = (props) => {
    const image = props.image;
    if (!image) {
      return null;
    }
    return (
      <DownloadWrapper>
        <Button
          onPress={() => handleDownload(image)}
          icon="download"
          shape="round"
        />
      </DownloadWrapper>
    );
  };

  // TODO: fix the positioning of the media
  const renderMessageImage = (props: RenderMessageImageProps<IMessage>) => {
    const { currentMessage } = props;
    if (currentMessage?.image) {
      return (
        <>
          <Chat image={currentMessage?.image} />
          <MessageImage
            {...props}
            containerStyle={{
              margin: 5,
              alignSelf: 'center',
              alignItems: 'center',
              maxWidth: initialWidth,
              justifyContent: 'center',
            }}
            imageStyle={{
              resizeMode: 'cover',
              height: initialHeight,
              width: initialWidth * 0.97,
              borderRadius: BASE_SIZE[12],
            }}
          />
        </>
      );
    }
    return null;
  };
  const renderMessageText = (props: RenderMessageTextProps<IMessage>) => {
    const { currentMessage } = props;
    if (currentMessage?.text) {
      return (
        <Text
          color={'black'}
          variant="body"
          style={{
            paddingVertical: 5,
            paddingHorizontal: 10,
            lineHeight: 18,
          }}
        >
          {props.currentMessage?.text}
        </Text>
      );
    }
  };

  const AudioWrapper = styled(Col)({
    flex: 1,
    margin: 5,
    padding: 10,
    justifyContent: 'center',
    backgroundColor: '#E3E5EE',
    width: initialWidth * 0.97,
    borderRadius: BASE_SIZE[12],
  });

  const AudioMessage: React.FC<RenderMessageAudioProps<IMessage>> = (props) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const { currentMessage } = props;

    const player = useAudioPlayer(currentMessage?.audio || null);

    const playAudio = async () => {
      if (player.playing) {
        player.pause();
        setIsPlaying(false);
      } else {
        player.play();
        setIsPlaying(true);
      }
    };

    return (
      <AudioWrapper>
        <Pressable onPress={playAudio}>
          <Row>
            <Icon
              name={isPlaying ? 'pause' : 'play-arrow'}
              size={30}
              color={'black'}
            />
            <Col alignItems="center" justifyContent="center" fullWidth flex={2}>
              <Image
                style={{ flex: 1, width: '100%', resizeMode: 'cover' }}
                source={require('assets/images/audio-wave.svg')}
              />
            </Col>
          </Row>
        </Pressable>
      </AudioWrapper>
    );
  };

  const VideoWrapper = styled(View)({
    marginVertical: 5,
    width: initialWidth,
    alignItems: 'center',
    height: initialHeight,
    justifyContent: 'center',
    borderRadius: BASE_SIZE[12],
  });

  const VideoMessage: React.FC<RenderMessageVideoProps<IMessage>> = (props) => {
    const { currentMessage } = props;

    const video = currentMessage?.video;

    const player = useVideoPlayer(video || null, (player) => {
      player.play();
      player.volume = 1.0;
    });

    return (
      <VideoWrapper>
        <VideoView
          player={player}
          allowsFullscreen
          allowsPictureInPicture
          style={{
            flex: 1,
            margin: 'auto',
            height: initialHeight,
            width: initialWidth * 0.97,
            borderRadius: BASE_SIZE[12],
          }}
          contentFit="cover"
          nativeControls
        />
      </VideoWrapper>
    );
  };

  const CardWrapper = styled(Col)({
    flex: 1,
    margin: 5,
    padding: 10,
    justifyContent: 'center',
    backgroundColor: '#E3E5EE',
    width: !isSmall ? initialWidth / 2 : undefined,
    borderRadius: BASE_SIZE[12],
  });

  const ContactMessage: React.FC<RenderMessageTextProps<IMessageProps>> = (
    props,
  ) => {
    const { currentMessage } = props;

    if (!currentMessage?.contact) {
      return null;
    }

    const isContact = currentMessage.contact;

    return (
      <CardWrapper>
        <Pressable onPress={() => handleDownload(isContact)}>
          <Row alignItems="center">
            <Icon name={'account-circle'} size={36} color={'black'} />
            <Spacer width={10} />
            <Text variant="subheading" color={'black'}>
              {strings.MediaDownloadContactDetails}
            </Text>
          </Row>
        </Pressable>
      </CardWrapper>
    );
  };

  const DocumentWrapper = styled(Col)({
    height: 100,
    opacity: 0.5,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'white',
    borderRadius: BASE_SIZE[4],
  });
  const DocumentFileMessage: React.FC<RenderMessageTextProps<IMessageProps>> = (
    props,
  ) => {
    const { currentMessage } = props;

    if (!currentMessage?.documentFile || !currentMessage?.contentType) {
      return null;
    }

    const documentFile = currentMessage.documentFile;
    const contentType = allowedExtensionTypeMap[currentMessage.contentType];

    return (
      <CardWrapper>
        <Pressable onPress={() => handleDownload(documentFile)}>
          <DocumentWrapper>
            <Text variant="dashboard-value-large" color={'black'}>
              {`${contentType?.toLocaleUpperCase()}`}
            </Text>
          </DocumentWrapper>
          <Row alignItems="center">
            <Icon name={'file-download'} size={BASE_SIZE[32]} color={'black'} />
            <Spacer width={10} />
            <Text variant="subheading" color={'black'}>
              {`${strings.MediaDownloadSubheading} ${contentType} file`}
            </Text>
          </Row>
        </Pressable>
      </CardWrapper>
    );
  };

  const CustomViewMessage: React.FC<RenderMessageTextProps<IMessageProps>> = (
    props,
  ) => {
    const { currentMessage } = props;

    return (
      <>
        <DocumentFileMessage position="left" currentMessage={currentMessage} />
        <ContactMessage position="left" currentMessage={currentMessage} />
      </>
    );
  };

  // TODO: resize bubble if theres media
  return (
    <Bubble
      {...props}
      wrapperStyle={wrapperStyle}
      renderTime={renderTime}
      renderMessageImage={renderMessageImage}
      renderMessageText={renderMessageText}
      renderMessageAudio={(props) => <AudioMessage {...props} />}
      renderMessageVideo={(props) => <VideoMessage {...props} />}
      renderCustomView={(props: BubbleProps<IMessageProps>) => (
        <CustomViewMessage {...props} />
      )}
    />
  );
};
